import { useMemo, useState } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'

import { DangerModal } from '@/lib/core/components/Modal'
import { DetailResponseError, ErrorType } from '@/lib/core/interfaces/common.types'

import { useCancelSmartShare } from '@/lib/features/SmartShares/api'
import messages from '@/lib/features/SmartShares/components/CancelSmartShareModal/CancelSmartShareModal.messages'
import { ShareId } from '@/lib/features/SmartShares/interfaces'

type CancelSmartShareModalProps = {
  closeModal: (confirmed?: boolean) => void
  shareIds: ShareId[]
  isOpen: boolean
}

export const CancelSmartShareModal = ({
  closeModal,
  shareIds = [],
  isOpen = false,
}: CancelSmartShareModalProps) => {
  const { formatMessage } = useIntl()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const { trigger: cancelSmartShare } = useCancelSmartShare()

  const singleCancelProcess = async (singleShareId: ShareId) => {
    try {
      await cancelSmartShare({ params: { id: singleShareId } })
    } catch (err) {
      const apiErrors = err as DetailResponseError

      if (apiErrors?.detail.includes(ErrorType.NOTFOUND)) {
        setError(formatMessage(messages.errorNotFound, { singleShareId }))
      } else {
        setError(formatMessage(messages.unknowError))
      }
    }
  }

  const onConfirm = async () => {
    setIsLoading(true)
    setError('')

    if (!cancelMode) {
      setError(formatMessage(messages.noSelected))
      setIsLoading(false)
      return
    }

    if (cancelMode === 'single') {
      await singleCancelProcess(shareIds[0])
    } else {
      await Promise.allSettled(shareIds.map(singleCancelProcess))
    }
    closeModal(true)
    setIsLoading(false)
  }

  const cancelMode = useMemo(() => {
    if (shareIds.length === 0) {
      return null
    }
    return shareIds.length > 1 ? 'multiple' : 'single'
  }, [shareIds])

  const isActive = useMemo(() => {
    if (!isOpen || !cancelMode) {
      return false
    }
    if (cancelMode === 'multiple') {
      return shareIds.length > 1
    }
    return !!shareIds[0]
  }, [shareIds, isOpen, cancelMode])

  return (
    <DangerModal
      isActive={isActive}
      title={formatMessage(messages.title)}
      cancelProps={{ disabled: isLoading }}
      cancelLabel={formatMessage(messages.cancelButton)}
      confirmLabel={
        error ? formatMessage(messages.tryAgain) : formatMessage(messages.confirmButton)
      }
      confirmProps={{ isLoading }}
      onCancel={() => closeModal(false)}
      onConfirm={onConfirm}
    >
      {error || <FormattedMessage {...messages.modalMessage} />}
    </DangerModal>
  )
}
